import React, { useState, useEffect } from "react";
import { useAppContext } from "../../functions/ContextAPI";
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import DocumentPreview from "../../components/DocumentPreview";

/**
 * DAP Documents Page Component
 * Displays all uploaded documents including self-study, evaluation, and expected attachments
 */
const DAPDocuments = () => {
  const context = useAppContext();
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const currentProgramme = context.selectedProgramme;
  const accreditationId = currentProgramme?.accreditationId || currentProgramme?._id;

  // Fetch all uploaded documents on mount
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!accreditationId) {
        return;
      }

      setIsLoading(true);
      try {
        const { getAccreditationDocuments } = await import("../../service/templateService");
        const data = await getAccreditationDocuments(accreditationId);

        // Collect all uploaded documents
        const docsArray = [];

        // Self-study form (if uploaded)
        if (data.selfStudyForm?.isUploaded && data.selfStudyForm?.uploadPath) {
          docsArray.push({
            id: `self-study-${data.selfStudyForm._id}`,
            name: "Self-Study Form (Uploaded)",
            type: "PDF",
            description: "Uploaded self-study form document",
            category: "Self-Study",
            uploadPath: data.selfStudyForm.uploadPath,
            fileName: data.selfStudyForm.fileName,
            size: data.selfStudyForm.fileSize,
            isUploaded: true,
            uploadedDate: data.selfStudyForm.uploadDate,
            source: "Institution Upload",
          });
        }

        // Evaluation form (if uploaded)
        if (data.evaluationForm?.isUploaded && data.evaluationForm?.uploadPath) {
          docsArray.push({
            id: `evaluation-${data.evaluationForm._id}`,
            name: "Programme Evaluation Form (Uploaded)",
            type: "PDF",
            description: "Uploaded programme evaluation document",
            category: "Evaluation",
            uploadPath: data.evaluationForm.uploadPath,
            fileName: data.evaluationForm.fileName,
            size: data.evaluationForm.fileSize,
            isUploaded: true,
            uploadedDate: data.evaluationForm.uploadDate,
            source: "Institution Upload",
          });
        }

        // Expected attachments (only uploaded ones)
        if (data.expectedDocuments && Array.isArray(data.expectedDocuments)) {
          data.expectedDocuments.forEach((doc) => {
            if (doc.isUploaded && doc.uploadPath) {
              docsArray.push({
                id: doc._id || doc.expectedId,
                name: doc.title,
                type: "PDF",
                description: doc.description,
                category: doc.category || "General",
                uploadPath: doc.uploadPath,
                size: null, // Size not provided in expected docs
                isUploaded: true,
                uploadedDate: doc.uploadDate,
                source: "Institution Upload",
                required: doc.required,
              });
            }
          });
        }

        setUploadedDocuments(docsArray);
      } catch (error) {
        console.error("Error fetching uploaded documents:", error);
        toast.error("Failed to load uploaded documents");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [accreditationId]);

  const handlePreview = (document) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  const handleDownload = (document) => {
    if (document.uploadPath) {
      const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const downloadUrl = `${apiBaseUrl}/download/${document.uploadPath}`;

      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = document.fileName || document.name;
      link.click();

      toast.success(`Downloading ${document.name}...`);
    } else {
      toast.warning("Document file not available for download");
    }
  };

  if (!currentProgramme) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-yellow-50 rounded-lg border-2 border-yellow-200 p-8 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">
              No Programme Selected
            </h3>
            <p className="text-yellow-800">
              Please select a programme to view DAP uploaded documents.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-blue-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <FolderOpenIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">DAP Documents</h1>
              <p className="text-gray-600 mt-1">
                Department Accreditation Portal - Institution uploaded documents
              </p>
            </div>
          </div>

          {/* Selected Programme Info */}
          {currentProgramme && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm font-semibold text-blue-900">
                Current Programme: {currentProgramme.programmeName || "N/A"}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                {currentProgramme.institutionName || "N/A"}
              </p>
            </div>
          )}
        </div>

        {/* Documents Section */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Uploaded Documents</h2>
              <p className="text-sm text-gray-600 mt-1">
                All documents uploaded by the institution for {currentProgramme.programmeName}
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading uploaded documents...</p>
              </div>
            </div>
          ) : uploadedDocuments.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {uploadedDocuments.map((doc) => (
                <UploadedDocumentCard
                  key={doc.id}
                  document={doc}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-12 text-center">
              <FolderOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Uploaded Documents</h3>
              <p className="text-gray-600">The institution has not uploaded any documents yet.</p>
            </div>
          )}
        </div>

        {/* Document Preview Modal */}
        {showPreview && selectedDocument && (
          <DocumentPreview
            isOpen={showPreview}
            onClose={() => {
              setShowPreview(false);
              setSelectedDocument(null);
            }}
            document={{
              name: selectedDocument.name,
              description: selectedDocument.description,
              uri: selectedDocument.uploadPath
                ? `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/download/${selectedDocument.uploadPath}`
                : null,
              type: selectedDocument.type?.toLowerCase(),
            }}
            downloadUrl={
              selectedDocument.uploadPath
                ? `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/download/${selectedDocument.uploadPath}`
                : null
            }
          />
        )}
      </div>
    </div>
  );
};

/**
 * Uploaded Document Card Component
 * Displays documents that have been uploaded by the institution with preview/download options
 */
const UploadedDocumentCard = ({ document, onDownload, onPreview }) => {
  const getCategoryColor = (category) => {
    const colors = {
      "Self-Study": "bg-purple-100 text-purple-800 border-purple-200",
      Evaluation: "bg-green-100 text-green-800 border-green-200",
      Academic: "bg-blue-100 text-blue-800 border-blue-200",
      Staffing: "bg-green-100 text-green-800 border-green-200",
      Facilities: "bg-purple-100 text-purple-800 border-purple-200",
      Library: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Administration: "bg-indigo-100 text-indigo-800 border-indigo-200",
      Quality: "bg-pink-100 text-pink-800 border-pink-200",
      General: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[category] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <div className="bg-white border-2 border-green-200 rounded-lg p-5 hover:border-green-400 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-base font-semibold text-gray-900">{document.name}</h3>
              {document.category && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${getCategoryColor(
                    document.category
                  )}`}
                >
                  {document.category}
                </span>
              )}
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                <CheckCircleIcon className="h-3 w-3 mr-1" />
                Uploaded
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">{document.description}</p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span className="flex items-center space-x-1">
                <span className="font-medium">Type:</span>
                <span>{document.type}</span>
              </span>
              {document.size && (
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Size:</span>
                  <span>{document.size}</span>
                </span>
              )}
              {document.uploadedDate && (
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Uploaded:</span>
                  <span>{new Date(document.uploadedDate).toLocaleDateString()}</span>
                </span>
              )}
              {document.source && (
                <span className="flex items-center space-x-1">
                  <span className="font-medium">Source:</span>
                  <span>{document.source}</span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          {/* Preview Button */}
          {onPreview && document.uploadPath && (
            <button
              onClick={() => onPreview(document)}
              className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border-2 border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all duration-200 flex items-center space-x-2"
            >
              <EyeIcon className="h-4 w-4" />
              <span>Preview</span>
            </button>
          )}
          {/* Download Button */}
          <button
            onClick={() => onDownload(document)}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border-2 border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 flex items-center space-x-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>Download</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DAPDocuments;
